import { Controller } from "@hotwired/stimulus"

/**
 * FM Account Controller
 * FM 계정 관리 (활성화/비활성화/삭제)
 */
export default class extends Controller {
  static targets = ["deactivateModal", "activateModal", "deleteModal"]

  static values = {
    fmId: String,
    fmName: String
  }

  // 비활성화 모달 열기
  openDeactivateModal(event) {
    event.preventDefault()
    if (this.hasDeactivateModalTarget) {
      this.deactivateModalTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
      if (typeof lucide !== 'undefined') {
        lucide.createIcons()
      }
    }
  }

  // 비활성화 모달 닫기
  closeDeactivateModal() {
    if (this.hasDeactivateModalTarget) {
      this.deactivateModalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  // 비활성화 확인
  async confirmDeactivate() {
    const confirmButton = this.deactivateModalTarget.querySelector('[data-modal-target="confirmButton"]')
    if (confirmButton) {
      confirmButton.disabled = true
      confirmButton.textContent = '처리 중...'
    }

    try {
      const response = await fetch(`/workplace/organization/field_managers/${this.fmIdValue}/deactivate`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const result = await response.json()

      if (result.success) {
        this.closeDeactivateModal()
        location.reload()
      } else {
        alert(result.error?.message || '비활성화 처리 중 오류가 발생했습니다.')
        if (confirmButton) {
          confirmButton.disabled = false
          confirmButton.textContent = '비활성화'
        }
      }
    } catch (error) {
      console.error('Deactivate error:', error)
      alert('비활성화 처리 중 오류가 발생했습니다.')
      if (confirmButton) {
        confirmButton.disabled = false
        confirmButton.textContent = '비활성화'
      }
    }
  }

  // 활성화 모달 열기
  openActivateModal(event) {
    event.preventDefault()
    if (this.hasActivateModalTarget) {
      this.activateModalTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
      if (typeof lucide !== 'undefined') {
        lucide.createIcons()
      }
    }
  }

  // 활성화 모달 닫기
  closeActivateModal() {
    if (this.hasActivateModalTarget) {
      this.activateModalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  // 활성화 확인
  async confirmActivate() {
    const confirmButton = this.activateModalTarget.querySelector('[data-modal-target="confirmButton"]')
    if (confirmButton) {
      confirmButton.disabled = true
      confirmButton.textContent = '처리 중...'
    }

    try {
      const response = await fetch(`/workplace/organization/field_managers/${this.fmIdValue}/activate`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const result = await response.json()

      if (result.success) {
        this.closeActivateModal()
        location.reload()
      } else {
        alert(result.error?.message || '활성화 처리 중 오류가 발생했습니다.')
        if (confirmButton) {
          confirmButton.disabled = false
          confirmButton.textContent = '활성화'
        }
      }
    } catch (error) {
      console.error('Activate error:', error)
      alert('활성화 처리 중 오류가 발생했습니다.')
      if (confirmButton) {
        confirmButton.disabled = false
        confirmButton.textContent = '활성화'
      }
    }
  }

  // 삭제 모달 열기
  openDeleteModal(event) {
    event.preventDefault()
    if (this.hasDeleteModalTarget) {
      this.deleteModalTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
      if (typeof lucide !== 'undefined') {
        lucide.createIcons()
      }
    }
  }

  // 삭제 모달 닫기
  closeDeleteModal() {
    if (this.hasDeleteModalTarget) {
      this.deleteModalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  // 삭제 확인
  async confirmDelete() {
    const confirmButton = this.deleteModalTarget.querySelector('[data-modal-target="confirmButton"]')
    if (confirmButton) {
      confirmButton.disabled = true
      confirmButton.textContent = '처리 중...'
    }

    try {
      const response = await fetch(`/workplace/organization/field_managers/${this.fmIdValue}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const result = await response.json()

      if (result.success) {
        this.closeDeleteModal()
        window.location.href = '/workplace/organization/field_managers'
      } else {
        alert(result.error?.message || '계정 삭제 중 오류가 발생했습니다.')
        if (confirmButton) {
          confirmButton.disabled = false
          confirmButton.textContent = '삭제'
        }
      }
    } catch (error) {
      console.error('Delete error:', error)
      alert('계정 삭제 중 오류가 발생했습니다.')
      if (confirmButton) {
        confirmButton.disabled = false
        confirmButton.textContent = '삭제'
      }
    }
  }

  // ESC 키로 모달 닫기
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDeactivateModal()
      this.closeActivateModal()
      this.closeDeleteModal()
    }
  }

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }
}
